import { useEffect, useState } from 'react';

import { FileOfflineStatusEnum } from '../../../enums/FileOfflineStatusEnum';
import type File from '../../../interfaces/File';
import type { FileOffline } from '../../../interfaces/FileApiService';
import type AuthenticatedUser from '../../../interfaces/AuthenticatedUser';
import { toAbsoluteServiceUrl } from '../../../lib/absoluteServiceUrl';
import { isNetworkFailureError } from '../../../utils/isTransientNetworkError';
import { fileService } from '../services';
import { FILES_OFFLINE_POLL_MS } from '../constants';

/**
 * Custom hook for polling the offline/download status of currently visible files.
 *
 * The hook starts polling the desktop agent for the status of the files, as long as:
 *   - User is online
 *   - Remote grant is enabled
 *   - The files array is non-empty
 *
 * Polling is stopped when:
 *   - All file rows report the terminal status (Downloaded), or
 *   - The agent becomes unreachable (network refusal)
 *
 * @param input.files Files currently shown in the UI; their IDs are sent to the agent.
 * @param input.parentFolder Optionally, parent folder ID for batch offline check.
 * @param input.authenticatedUser Current user; used for remote grant check and userId.
 * @param input.token Access token for agent API.
 * @param input.refreshToken Refresh token for agent API.
 *
 * @returns
 *   - filesOffline: Array of offline file statuses
 *   - filesOfflinePollingBump: Increment to force re-polling
 *   - setFilesOfflinePollingBump: Setter for bump
 */
export function useFilesOfflinePolling(input: {
  files: File[];
  parentFolder: File | null;
  authenticatedUser: AuthenticatedUser | null | undefined;
  token: string | null | undefined;
  refreshToken: string | undefined;
}) {
  // Destructure inputs
  const { files, parentFolder, authenticatedUser, token, refreshToken } = input;

  // State to hold latest offline status for each file
  const [filesOffline, setFilesOffline] = useState<FileOffline[]>([]);

  // Counter for triggering manual polling
  const [filesOfflinePollingBump, setFilesOfflinePollingBump] = useState(0);

  useEffect(() => {
    // Bail out early (and clear old state) if no files to poll
    if (files.length === 0) {
      setFilesOffline([]);
      return;
    }

    // Only poll if user is online
    if (!navigator.onLine) {
      return;
    }

    // Only poll if remote grant is enabled for user (required for agent access)
    if (!authenticatedUser?.isRemoteGrantEnabled) {
      return;
    }

    // Control flags for the polling loop
    let cancelled = false; // Used to mark if polling should stop (effect cleanup)
    let intervalId: number | null = null; // To store the interval timer id
    let agentUnavailable = false; // Set true if we detect network refusal

    // Extract file ids to check
    const fileIds = files.map((f) => f.id);

    /**
     * Type guard: true iff val is an array of objects with id and status fields.
     */
    const isFilesOfflineArray = (val: unknown): val is FileOffline[] => {
      if (!Array.isArray(val)) return false;
      return val.every((r) => r && typeof r === 'object' && 'id' in r && 'status' in r);
    };

    /**
     * Sanitize API response:
     *  - Only keep rows with known id and valid status value
     *  - Filter to only the fileIds we asked for
     */
    const sanitizeResponse = (resp: FileOffline[]) => {
      const validStatuses = new Set(Object.values(FileOfflineStatusEnum));
      return resp.filter(
        (r) =>
          (typeof r.id === 'string' || typeof r.id === 'number') &&
          // Allow both string and number ids for comparison with local ids
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          fileIds.includes(r.id as any) &&
          validStatuses.has(r.status),
      );
    };

    /**
     * Test if all files are in Downloaded state, or if response is empty/invalid.
     * Used to determine if polling should cease.
     */
    const allDone = (resp: unknown) =>
      !Array.isArray(resp) ||
      resp.length === 0 ||
      resp.every(
        (r: { status?: FileOfflineStatusEnum }) => r?.status === FileOfflineStatusEnum.Downloaded,
      );

    /**
     * Helper to stop the polling interval (if running)
     */
    const stop = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    /** Detect if an error is a network refusal (e.g., agent isn't reachable). */
    const isNetworkRefusal = (err: unknown) => {
      if (!navigator.onLine) return false;
      return isNetworkFailureError(err);
    };

    /**
     * Run one polling request.
     */
    const run = async () => {
      // Prevent re-entrant or redundant runs
      if (cancelled || agentUnavailable) return;

      // Stop polling if user is no longer online
      if (!navigator.onLine) {
        stop();
        return;
      }

      try {
        // Ask backend for offline statuses
        const resp = await fileService.checkFilesOffline({
          fileIds,
          parentId: parentFolder?.id,
          webApp: window.location.origin,
          userId: authenticatedUser?.id,
          token: token || '',
          refreshToken: refreshToken || '',
          server: toAbsoluteServiceUrl(import.meta.env.VITE_APP_API_BASE_URL),
        });

        // Do not update state if we've been cancelled
        if (cancelled) return;

        // Ensure API returned expected shape
        if (!isFilesOfflineArray(resp)) {
          console.warn('[filesOffline] Unexpected response shape, stopping poll:', resp);
          stop();
          return;
        }

        // Filter out bad rows and set state
        const safe = sanitizeResponse(resp);
        setFilesOffline(safe);

        // If everyone is Downloaded, or response is empty, stop polling
        if (allDone(safe)) {
          stop();
        }
      } catch (err) {
        // Suppress after unmount/cancel
        if (cancelled) return;

        // If error is fatal (network/agent refusal), cease polling entirely
        if (isNetworkRefusal(err)) {
          agentUnavailable = true;
          stop();
          return;
        }
      }
    };

    // Kick off first run immediately
    void run();

    // Schedule regular polling
    intervalId = window.setInterval(run, FILES_OFFLINE_POLL_MS);

    // Clean up polling on effect cleanup/change
    return () => {
      cancelled = true;
      stop();
    };
  }, [
    authenticatedUser?.isRemoteGrantEnabled,
    parentFolder?.id,
    files.map((f) => f.id).join(','),
    filesOfflinePollingBump,
    authenticatedUser?.id,
    token,
    refreshToken,
  ]);

  return { filesOffline, filesOfflinePollingBump, setFilesOfflinePollingBump };
}
